import { Label, Input, Textarea, Button } from '@fluentui/react-components';
import { authentication } from '@microsoft/teams-js';
import React, { useMemo, useCallback, useState } from 'react';
import {
    BroadcastMessageData,
    BroadcastMessageWithStatistic,
    BroadcastRequestSource,
} from '../../model';
import BotSubmitAction from '../../model/BotSubmitAction';
import { IBotSubmitPayload } from '../../model/IBotSubmitPayload';
import { submitDialog } from '../../utils/teams';
import { useBroadcastMessagingDialogPageStyles as useBroadcastMessageFormStyles } from './BroadcastMessageForm.styles';
import { useTranslation } from 'react-i18next';
import SubmitButton from '../../controls/Buttons/SubmitButton';

export interface BroadcastMessageFormProps {
    flightTeamIds: string[];
    broadcastMessage?: BroadcastMessageWithStatistic;
}

const useInitialisedValue = (initialValue: string | undefined) => {
    const [value, setValue] = useState(initialValue ?? '');
    const [editable, setEditable] = useState(!initialValue);

    const changeValue = useCallback(
        (newValue: string) => {
            if (!editable) {
                return;
            }

            setValue(newValue);
        },
        [editable]
    );

    const resetValue = useCallback(
        (editable: boolean) => {
            setValue(initialValue ?? '');
            setEditable(editable);
        },
        [initialValue]
    );

    return {
        value,
        editable,
        setValue: changeValue,
        resetValue,
    };
};

type UseInitialisedValueResult = ReturnType<typeof useInitialisedValue>;

const EditFieldButton: React.FC<{ value: UseInitialisedValueResult }> = ({
    value,
}) => {
    const { t } = useTranslation();

    return (
        <Button
            appearance="transparent"
            onClick={() => value.resetValue(!value.editable)}
        >
            {value.editable ? t('Reset') : t('Edit')}
        </Button>
    );
};

const BroadcastMessageForm: React.FC<BroadcastMessageFormProps> = ({
    flightTeamIds,
    broadcastMessage,
}) => {
    const { t } = useTranslation();
    const styles = useBroadcastMessageFormStyles();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const messageTitle = useInitialisedValue(broadcastMessage?.title);
    const message = useInitialisedValue(broadcastMessage?.content);

    const isSubmitDisable = useMemo(() => {
        const disable = isSubmitting;
        if (disable) {
            return true;
        }
        const text = message.value.trim() || '';
        return text.length === 0 || text.length > 4000;
    }, [isSubmitting, message]);

    const submitCallback = useCallback(async () => {
        if (isSubmitDisable) {
            return;
        }
        setIsSubmitting(true);

        const token = await authentication.getAuthToken();
        const text =
            broadcastMessage && !message.editable
                ? null
                : message.value.trim() || '';

        const submitRequest: IBotSubmitPayload<BroadcastMessageData> = {
            data: {
                existingMessageId: broadcastMessage?.id,
                flightTeamIds: flightTeamIds,
                title:
                    broadcastMessage && !messageTitle.editable
                        ? null
                        : messageTitle.value,
                content: text,
                requestSource: BroadcastRequestSource.DepartureBoard,
            },
            appUserAccessToken: token,
            action: BotSubmitAction.BroadcastMessaging,
        };
        submitDialog(submitRequest);
    }, [
        flightTeamIds,
        broadcastMessage,
        isSubmitDisable,
        message,
        messageTitle,
    ]);

    return (
        <div className={styles.root}>
            <div className={styles.formField}>
                <span>
                    <Label weight="semibold" required>
                        Title
                    </Label>
                    {broadcastMessage && (
                        <EditFieldButton value={messageTitle} />
                    )}
                </span>
                <Input
                    placeholder="Broadcast message Title"
                    value={messageTitle.value}
                    onChange={(_, data) => {
                        messageTitle.setValue(data.value);
                    }}
                    disabled={!messageTitle.editable}
                />
            </div>
            <div className={styles.formField}>
                <span>
                    <Label weight="semibold" required>
                        {t('Message')}
                    </Label>
                    {broadcastMessage && <EditFieldButton value={message} />}
                </span>
                <Textarea
                    rows={5}
                    value={message.value}
                    onChange={(_, data) => {
                        message.setValue(data.value);
                    }}
                    disabled={!message.editable}
                />
            </div>
            <div className={styles.actions}>
                <SubmitButton
                    disabled={isSubmitDisable || isSubmitting}
                    onSubmit={submitCallback}
                >
                    {t('Broadcast')}
                </SubmitButton>
            </div>
        </div>
    );
};

export default BroadcastMessageForm;
